/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-lone-blocks */
import * as React from 'react';
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { ContentServiceV1 } from '@oneaudi/content-service';
import { ContentContextProvider, FootnoteContextProvider } from '@oneaudi/feature-app-utils';
import type { AudiFootnoteRefernceServiceScopeManagerInterfaceV3 } from '@oneaudi/footnote-reference-service';
import { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import { RenderModeServiceV1 } from '@oneaudi/render-mode-service';
import { OneGraphServiceV1 } from '@oneaudi/onegraph-service';
import type { Logger } from '@feature-hub/logger';
import { EditorialTeaserContentLoader } from './components/EditorialTeasers';
import {
  ChunkedEditorialTeaserComponent,
  EditorialTeaserComponent,
  EditorialTeaserContent,
} from '../types';
import { EditorJsonContent, FalconContent, mapContent } from './contentMapping/contentMapping';
import { getDesignLibrary } from '../alpha-helpers';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'audi-tracking-service': TrackingServiceV2;
  readonly 'audi-content-service': ContentServiceV1;
  readonly 'audi-footnote-reference-service': AudiFootnoteRefernceServiceScopeManagerInterfaceV3;
  readonly 'locale-service': LocaleServiceV1;
  readonly 's2:async-ssr-manager'?: AsyncSsrManagerV1;
  readonly 's2:serialized-state-manager'?: SerializedStateManagerV1;
  readonly 'audi-render-mode-service': RenderModeServiceV1;
  readonly 'onegraph-service': OneGraphServiceV1;
}

{
  // allow setting ssr mode via URL, so it's set before anything launches
  if (typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search);
    let ssr = false;
    if (searchParams.has('ssr')) {
      const ssrQueryParam = searchParams.get('ssr') as string;
      ssr = ssrQueryParam === 'true';
      // eslint-disable-next-line no-console
      console.debug(`Query parameter 'ssr' recognized: ${ssr}`);
    }

    // eslint-disable-next-line no-underscore-dangle
    (window as any).__SSR__ = ssr;
  }
}

function sliceComponentsIntoChunks(
  components: EditorialTeaserComponent[],
  chunkSize: number,
): ChunkedEditorialTeaserComponent[][] {
  const chunkedComponents: ChunkedEditorialTeaserComponent[][] = [];
  let chunk: ChunkedEditorialTeaserComponent[] = [];

  components.forEach((component, componentIndex) => {
    chunk.push({ ...component, originalIndex: componentIndex });
    if (chunk.length >= chunkSize) {
      chunkedComponents.push(chunk);
      chunk = [];
    }
  });

  if (chunk.length) {
    chunkedComponents.push(chunk);
  }

  return chunkedComponents;
}

export const getAsyncStateHolder = async (contentService: ContentServiceV1) => {
  const rawContent = (await contentService?.getContent()) as EditorJsonContent | FalconContent;
  const content = mapContent(rawContent);
  const chunkedComponents = sliceComponentsIntoChunks(content?.components, 3);
  return { content, chunkedComponents };
};

// eslint-disable-next-line import/no-default-export
const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    externals: {
      react: '^17.0.2 || ^18.3.0',
      'react-dom': '^17.0.2 || ^18.3.0',
      'styled-components': '^5.1.1',
      '@feature-hub/react': '^3.5.0',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.13.0' }),
    },
    featureServices: {
      's2:logger': '^1.0.0',
      'audi-content-service': '^1.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      'locale-service': '^1.0.0',
      'onegraph-service': '^1.0.0',
    },
  },
  optionalDependencies: {
    featureServices: {
      'audi-tracking-service': '^2.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-render-mode-service': '^1.0.0',
    },
  },

  create: ({
    featureServices: {
      'audi-content-service': contentService,
      'audi-footnote-reference-service': footnoteReferenceServiceScopeManager,
      'locale-service': localeService,
      's2:async-ssr-manager': asyncSSRManager,
      's2:serialized-state-manager': serializedStateManager,
      'audi-render-mode-service': renderModeService,
      'audi-tracking-service': trackingService,
      'onegraph-service': oneGraphService,
      's2:logger': loggerService,
    },
    config,
  }: FeatureAppEnvironment<FeatureServiceDependencies, EditorialTeaserContent>) => {
    let asyncStateHolder: any;

    // SSR
    // --------------------------------------------------------------------
    // on the server
    if (asyncSSRManager) {
      asyncSSRManager.scheduleRerender(
        (async () => {
          asyncStateHolder = await getAsyncStateHolder(contentService);
          serializedStateManager?.register(() => JSON.stringify(asyncStateHolder));
        })(),
      );
    } else {
      // on the client
      const serializedState = serializedStateManager?.getSerializedState();

      if (serializedState) {
        asyncStateHolder = JSON.parse(serializedState);
      } else {
        asyncStateHolder = () => getAsyncStateHolder(contentService);
      }
    }

    if (trackingService) {
      /* eslint-disable no-param-reassign */
      trackingService.featureAppName = __FEATURE_APP_NAME__;
    }
    const { OneGraphProvider } = oneGraphService;
    const requiredServiceForOneGraph = {
      'onegraph-service': oneGraphService,
      'locale-service': localeService,
      's2:async-ssr-manager': asyncSSRManager,
    };

    return {
      render: () => {
        return (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>
            {asyncStateHolder && (
              <ContentContextProvider contentService={contentService}>
                <OneGraphProvider featureServices={requiredServiceForOneGraph}>
                  <FootnoteContextProvider
                    /* eslint-disable-next-line max-len */
                    footnoteReferenceService={footnoteReferenceServiceScopeManager?.getDefaultScopeRefService()}
                  >
                    <UniversalEditorProvider
                      contentService={contentService}
                      renderModeService={renderModeService}
                    >
                      <EditorialTeaserContentLoader
                        oneGraphService={oneGraphService}
                        loggerService={loggerService}
                        localeService={localeService}
                        trackingService={trackingService}
                        initialContent={config}
                        ssrContent={asyncStateHolder.content}
                        chunkedComponents={asyncStateHolder.chunkedComponents}
                      />
                    </UniversalEditorProvider>
                  </FootnoteContextProvider>
                </OneGraphProvider>
              </ContentContextProvider>
            )}
          </>
        );
      },
    };
  },
};

export default featureAppDefinition;
